const clientId = process.env.REACT_APP_AUTOAPPY_DEVELOPER_OAUTH_CLIENT_ID?.trim();
const identityUrl = process.env.REACT_APP_AUTOAPPY_IDENTITY_URL?.replace(/\/$/, '');
const applicationKey = process.env.REACT_APP_CURRENT_APPLICATION || 'autoappy';
const tokenStorageKey = `autoappy.${applicationKey}.developer_tokens`;
const pendingStorageKey = `autoappy.${applicationKey}.developer_oauth_pending`;

let refreshPromise = null;
let loginPromise = null;
let callbackPromise = null;

export const developerOAuthCallbackPath = '/auth/autoappy/developer/callback';
export const isDeveloperOAuthEnabled = () => Boolean(clientId && identityUrl);

const redirectUri = () => process.env.REACT_APP_AUTOAPPY_DEVELOPER_REDIRECT_URI
  || `${window.location.origin}${developerOAuthCallbackPath}`;

const encodeBase64Url = (bytes) => {
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const randomValue = (length = 32) => {
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);
  return encodeBase64Url(bytes);
};

const codeChallenge = async (verifier) => {
  const digest = await window.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  );
  return encodeBase64Url(new Uint8Array(digest));
};

const normalizeReturnTo = (value) => {
  try {
    const target = new URL(value || '/', window.location.origin);
    if (target.origin !== window.location.origin) return '/';
    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return '/';
  }
};

const readTokens = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(tokenStorageKey)) || null;
  } catch {
    return null;
  }
};

const storeTokens = (payload, previousRefreshToken = null) => {
  const tokens = {
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token || previousRefreshToken,
    expiresAt: Date.now() + Math.max(0, Number(payload.expires_in || 0) - 30) * 1000,
  };
  window.sessionStorage.setItem(tokenStorageKey, JSON.stringify(tokens));
  return tokens;
};

const tokenRequest = async (payload) => {
  const response = await window.fetch(`${identityUrl}/oauth/token`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams(payload),
  });

  if (!response.ok) throw new Error('AutoAppy rejected the OAuth token request.');
  return response.json();
};

export const clearDeveloperTokens = () => {
  window.sessionStorage.removeItem(tokenStorageKey);
  window.sessionStorage.removeItem(pendingStorageKey);
};

export const beginDeveloperLogin = (returnTo = '/') => {
  if (!isDeveloperOAuthEnabled()) {
    return Promise.reject(new Error('Developer OAuth is not configured.'));
  }

  if (!loginPromise) {
    loginPromise = (async () => {
      const state = randomValue();
      const verifier = randomValue(64);
      const pending = {
        state,
        verifier,
        returnTo: normalizeReturnTo(returnTo),
        createdAt: Date.now(),
      };
      window.sessionStorage.setItem(pendingStorageKey, JSON.stringify(pending));

      const query = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri(),
        response_type: 'code',
        scope: 'identity apps workspaces platform',
        state,
        code_challenge: await codeChallenge(verifier),
        code_challenge_method: 'S256',
      });
      window.location.assign(`${identityUrl}/oauth/authorize?${query.toString()}`);
    })().catch((error) => {
      loginPromise = null;
      throw error;
    });
  }

  return loginPromise;
};

const completeDeveloperLoginRequest = async (search) => {
  const query = new URLSearchParams(search);
  let pending;
  try {
    pending = JSON.parse(window.sessionStorage.getItem(pendingStorageKey));
  } catch {
    pending = null;
  }

  if (query.get('error')) throw new Error('AutoAppy authorization was denied.');
  if (!pending || Date.now() - pending.createdAt > 10 * 60 * 1000) {
    throw new Error('The AutoAppy login request expired. Please try again.');
  }
  if (!query.get('state') || query.get('state') !== pending.state || !query.get('code')) {
    throw new Error('The AutoAppy login response could not be verified.');
  }

  window.sessionStorage.removeItem(pendingStorageKey);
  const payload = await tokenRequest({
    grant_type: 'authorization_code',
    client_id: clientId,
    redirect_uri: redirectUri(),
    code: query.get('code'),
    code_verifier: pending.verifier,
  });
  storeTokens(payload);

  return normalizeReturnTo(pending.returnTo);
};

export const completeDeveloperLogin = (search = window.location.search) => {
  if (!callbackPromise) {
    callbackPromise = completeDeveloperLoginRequest(search).catch((error) => {
      callbackPromise = null;
      throw error;
    });
  }

  return callbackPromise;
};

export const refreshDeveloperAccessToken = async () => {
  if (!isDeveloperOAuthEnabled()) return null;
  if (refreshPromise) return refreshPromise;

  const tokens = readTokens();
  if (!tokens?.refreshToken) return null;

  refreshPromise = tokenRequest({
    grant_type: 'refresh_token',
    client_id: clientId,
    refresh_token: tokens.refreshToken,
    scope: 'identity apps workspaces platform',
  }).then((payload) => storeTokens(payload, tokens.refreshToken).accessToken)
    .catch((error) => {
      clearDeveloperTokens();
      throw error;
    })
    .finally(() => { refreshPromise = null; });

  return refreshPromise;
};

export const getDeveloperAccessToken = async () => {
  if (!isDeveloperOAuthEnabled()) return null;
  const tokens = readTokens();
  if (!tokens) return null;
  if (tokens.accessToken && tokens.expiresAt > Date.now()) return tokens.accessToken;
  return refreshDeveloperAccessToken();
};
